import { momentDateTime } from '../dates';
type Source = { file: string; page?: number; quote?: string };
type Artifact = { id: string; fileName: string; sha256?: string };
export type ReviewTrailJob = {
  id: string;
  status: string;
  createdAt: string;
  result?: {
    owners: Array<{ parcel: string; owner: string; siteAddress: string; sourceFile: string; sourceUrl?: string; sources?: Source[] }>;
    documents: Array<{ instrument: string; title: string; pdfFile: string; sourceUrl?: string; recordedOn?: string; propertyMatch?: string;
      fields: Array<{ name: string; text: string; sources: Source[] }> }>;
    summary: string;
  };
  artifacts: Artifact[];
  sourceCaptures?: Array<{ step: string; url: string; artifactId: string; fileName: string; sha256: string; capturedAt: string }>;
  reviews: Array<{ decision: string; note: string; createdAt: string }>;
};

function evidenceButton(job: ReviewTrailJob, fileName: string, label: string, onDownload: (id: string) => void) {
  const artifact = job.artifacts.find(item => item.fileName === fileName);
  return artifact ? <button key={`${fileName}:${label}`} type="button" className="button button-quiet"
    onClick={() => onDownload(artifact.id)}>{label}</button> : <span key={`${fileName}:${label}`}>Evidence unavailable: {fileName}</span>;
}

function establishesProperty(document: { propertyMatch?: string }): boolean {
  return document.propertyMatch === undefined || document.propertyMatch === 'PARCEL_ID_MATCH' ||
    document.propertyMatch === 'STREET_ADDRESS_MATCH';
}

export function ResearchReviewTrail({ jobs, onDownload }: { jobs: ReviewTrailJob[]; onDownload: (id: string) => void }) {
  const current = jobs[0];
  if (!current) return null;
  const hasAnswer = !!current.result?.owners.length && !!current.result?.documents.some(establishesProperty);
  const prior = !hasAnswer ? jobs.slice(1).find(job => job.status === 'ACCEPTED' &&
    !!job.result?.owners.length && !!job.result?.documents.some(establishesProperty)) : undefined;
  const captures = current.sourceCaptures ?? [];
  return <section className="inline-card" aria-label="Research evidence trail">
    <h3>Research evidence trail</h3>
    {!hasAnswer && current.result && <p><strong>This run did not verify both a parcel owner and a recorded notice.</strong> Review the source attempts below. Do not treat earlier findings as freshly verified.</p>}
    {prior?.result && <div>
      <h4>Previously accepted findings</h4>
      <p>Accepted in an earlier run on {momentDateTime(prior.createdAt)}; the current run did not verify both findings.</p>
      {prior.result.owners.map(owner => <article key={owner.parcel}>
        <p><strong>Owner:</strong> {owner.owner} · parcel {owner.parcel} · {owner.siteAddress}</p>
        {owner.sourceUrl && <p><a href={owner.sourceUrl} target="_blank" rel="noreferrer">Open earlier parcel source</a></p>}
        {evidenceButton(prior, owner.sourceFile, 'Open earlier parcel evidence', onDownload)}
        {owner.sources?.map((source, index) => <details key={index}>
          <summary>Parcel citation: {source.file}{source.page ? `, page ${source.page}` : ''}</summary>
          {source.quote && <blockquote>{source.quote}</blockquote>}
        </details>)}
      </article>)}
      {prior.result.documents.map(document => <article key={document.instrument}>
        <p><strong>Notice:</strong> {document.title} · instrument {document.instrument}{document.recordedOn ? ` · recorded ${document.recordedOn}` : ''}</p>
        {document.sourceUrl && <p><a href={document.sourceUrl} target="_blank" rel="noreferrer">Open earlier notice source</a></p>}
        {evidenceButton(prior, document.pdfFile, 'Open earlier notice PDF', onDownload)}
        {document.fields.flatMap(field => field.sources.map((source, index) =>
          <details key={`${field.name}:${index}`}>
            <summary>{field.name}: {source.file}{source.page ? `, page ${source.page}` : ''}</summary>
            {source.quote && <blockquote>{source.quote}</blockquote>}
          </details>))}
      </article>)}
      {prior.reviews.filter(review => review.decision === 'ACCEPT').map((review, index) =>
        <p key={index}><strong>Earlier review:</strong> {review.note} ({momentDateTime(review.createdAt)})</p>)}
    </div>}
    {!!captures.length && <div>
      <h4>Source attempts in this run</h4>
      <ol>{captures.map(capture => <li key={`${capture.step}:${capture.artifactId}`}>
        <a href={capture.url} target="_blank" rel="noreferrer">{capture.url}</a>
        {' · '}{momentDateTime(capture.capturedAt)}{' · '}
        <button type="button" className="button button-quiet" onClick={() => onDownload(capture.artifactId)}>
          Open captured {capture.fileName}
        </button>
      </li>)}</ol>
    </div>}
    {!!current.artifacts.length && <details>
      <summary>All retained files for this run ({current.artifacts.length})</summary>
      <ul>{current.artifacts.map(artifact => <li key={artifact.id}>
        <button type="button" className="button button-quiet" onClick={() => onDownload(artifact.id)}>Open {artifact.fileName}</button>
        {artifact.sha256 && <small> · SHA-256 {artifact.sha256}</small>}
      </li>)}</ul>
    </details>}
  </section>;
}
